import { RJSFSchema, UiSchema } from '@rjsf/utils';
import { JsonPlaceholderReplacer } from 'json-placeholder-replacer';
import { parseJson } from 'src/utilities/ApiHelper';
import {
  SchemaAttributes,
  SchemaTranslator,
} from 'src/integration/ddm/DDMSchemaTranslationProvider';
import {
  CaseCreationStaticSchema,
  uiSchema,
} from 'src/components/Forms/CreateCase/CreateCaseSchema';
import { ITranslationContext } from 'src/state/providers/TranslationProvider';

export class ProductSafetySchemaTranslator implements SchemaTranslator {
  private readonly coreSchema: RJSFSchema;
  private readonly uiSchema: UiSchema;
  private readonly caseSummarySchema: RJSFSchema;
  private readonly createCaseStaticSchema: CaseCreationStaticSchema;
  private bundles: ITranslationContext;

  constructor(schemaAttributes: SchemaAttributes) {
    this.coreSchema = schemaAttributes.coreSchema;
    this.uiSchema = schemaAttributes.uiSchema;
    this.caseSummarySchema = schemaAttributes.caseSummarySchema;
    this.createCaseStaticSchema = schemaAttributes.createCaseStaticSchema;
    this.bundles = schemaAttributes.bundles;
  }

  translateCoreSchema(): RJSFSchema {
    return this.getPlaceholderReplacedCoreSchema(
      this.coreSchema,
      this.bundles.productSafetyBundle,
    );
  }

  translateUiSchema(): UiSchema {
    return this.getPlaceholderReplacedUiSchema(
      this.uiSchema,
      this.bundles.productSafetyBundle,
    );
  }

  translateCreateCaseStaticSchema(): CaseCreationStaticSchema {
    return this.getPlaceholderReplacedCreateCaseStaticSchema(
      this.createCaseStaticSchema,
      this.bundles.productSafetyBundle,
    );
  }

  translateCaseSummarySchema(): RJSFSchema {
    return this.getPlaceholderReplacedCaseSummarySchema(
      this.caseSummarySchema,
      this.bundles.productSafetyBundle,
    );
  }

  private getPlaceholderReplacedUiSchema(
    schema: UiSchema,
    bundle: any,
  ): UiSchema {
    const placeHolderReplacer = new JsonPlaceholderReplacer();

    placeHolderReplacer.addVariableMap({
      add_button_text: bundle.getMessage('add_button_text'),
      remove_button_text: bundle.getMessage('remove_button_text'),
      confirmation_modal_title: bundle.getMessage('confirmation_modal_title'),
      confirmation_modal_text: bundle.getMessage('confirmation_modal_text'),
      confirmation_submit_button_text: bundle.getMessage(
        'confirmation_submit_button_text',
      ),
      removal_information_section_heading: bundle.getMessage(
        'removal_information_section_heading',
      ),
      recall_information_section_heading: bundle.getMessage(
        'recall_information_section_heading',
      ),
      invalid_date_less_than_date_plus_one_error_message: bundle.getMessage(
        'invalid_date_less_than_date_plus_one_error_message',
      ),
      mandatory_field_error_message: bundle.getMessage(
        'mandatory_field_error_message',
      ),
    });

    return placeHolderReplacer.replace(parseJson(JSON.stringify(schema)));
  }

  private getPlaceholderReplacedCaseSummarySchema(
    schema: RJSFSchema,
    bundle: any,
  ): RJSFSchema {
    const placeHolderReplacer = new JsonPlaceholderReplacer();
    placeHolderReplacer.addVariableMap({
      summary_section_heading: bundle.getMessage('summary_section_heading'),
      status_field_name: bundle.getMessage('status_field_name'),
      status_option_open: bundle.getMessage('status_option_open'),
      status_option_resolved: bundle.getMessage('status_option_resolved'),
    });

    return placeHolderReplacer.replace(parseJson(JSON.stringify(schema)));
  }

  private getPlaceholderReplacedCoreSchema(
    schema: RJSFSchema,
    bundle: any,
  ): RJSFSchema {
    const placeHolderReplacer = new JsonPlaceholderReplacer();
    placeHolderReplacer.addVariableMap({
      referral_information_section_header: bundle.getMessage(
        'referral_information_section_header',
      ),
      full_name_field_name: bundle.getMessage('full_name_field_name'),
      email_field_name: bundle.getMessage('email_field_name'),
      agency_field_name: bundle.getMessage('agency_field_name'),
      country_field_name: bundle.getMessage('country_field_name'),
      role_field_name: bundle.getMessage('role_field_name'),
      request_summary_section_heading: bundle.getMessage(
        'request_summary_section_heading',
      ),
      request_title_field_name: bundle.getMessage('request_title_field_name'),
      case_description_field_name: bundle.getMessage(
        'case_description_field_name',
      ),
      request_type_section_heading: bundle.getMessage(
        'request_type_section_heading',
      ),
      request_type_checkbox_removal_of_product_from_sale: bundle.getMessage(
        'request_type_checkbox_removal_of_product_from_sale',
      ),
      request_type_checkbox_information_request: bundle.getMessage(
        'request_type_checkbox_information_request',
      ),
      request_type_checkbox_recall_from_end_user: bundle.getMessage(
        'request_type_checkbox_recall_from_end_user',
      ),
      request_type_checkbox_meeting_request: bundle.getMessage(
        'request_type_checkbox_meeting_request',
      ),
      request_type_checkbox_other_request: bundle.getMessage(
        'request_type_checkbox_other_request',
      ),
      request_type_checkbox_no_action_required_fyi: bundle.getMessage(
        'request_type_checkbox_no_action_required_fyi',
      ),
      removal_of_product_from_sale_section_heading: bundle.getMessage(
        'removal_of_product_from_sale_section_heading',
      ),
      need_by_date_field_name: bundle.getMessage('need_by_date_field_name'),
      link_to_public_notification_field_name: bundle.getMessage(
        'link_to_public_notification_field_name',
      ),
      issue_type_field_name: bundle.getMessage('issue_type_field_name'),
      issue_type_option_detailed_page_issue: bundle.getMessage(
        'issue_type_option_detailed_page_issue',
      ),
      issue_type_option_no_issue: bundle.getMessage(
        'issue_type_option_no_issue',
      ),
      issue_type_option_process_issue: bundle.getMessage(
        'issue_type_option_process_issue',
      ),
      issue_type_option_safety_issue_asphyxiation_hazard: bundle.getMessage(
        'issue_type_option_safety_issue_asphyxiation_hazard',
      ),
      issue_type_option_safety_issue_chemical_risk: bundle.getMessage(
        'issue_type_option_safety_issue_chemical_risk',
      ),
      issue_type_option_safety_issue_choking_hazard: bundle.getMessage(
        'issue_type_option_safety_issue_choking_hazard',
      ),
      issue_type_option_safety_issue_contaminant_risk: bundle.getMessage(
        'issue_type_option_safety_issue_contaminant_risk',
      ),
      issue_type_option_safety_issue_electric_shock_risk: bundle.getMessage(
        'issue_type_option_safety_issue_electric_shock_risk',
      ),
      issue_type_option_safety_issue_eye_injury_risk: bundle.getMessage(
        'issue_type_option_safety_issue_eye_injury_risk',
      ),
      issue_type_option_safety_issue_fire_hazard: bundle.getMessage(
        'issue_type_option_safety_issue_fire_hazard',
      ),
      issue_type_option_safety_issue_hearing_loss_risk: bundle.getMessage(
        'issue_type_option_safety_issue_hearing_loss_risk',
      ),
      issue_type_option_safety_issue_ingredient_risk: bundle.getMessage(
        'issue_type_option_safety_issue_ingredient_risk',
      ),
      issue_type_option_safety_issue_maximum_transmission_power_emitted:
        bundle.getMessage(
          'issue_type_option_safety_issue_maximum_transmission_power_emitted',
        ),
      issue_type_option_safety_issue_oral_ingestion_risk: bundle.getMessage(
        'issue_type_option_safety_issue_oral_ingestion_risk',
      ),
      issue_type_option_safety_issue_other_specify_issue: bundle.getMessage(
        'issue_type_option_safety_issue_other_specify_issue',
      ),
      issue_type_option_safety_issue_pesticide_residue: bundle.getMessage(
        'issue_type_option_safety_issue_pesticide_residue',
      ),
      issue_type_option_safety_issue_protection_missing: bundle.getMessage(
        'issue_type_option_safety_issue_protection_missing',
      ),
      issue_type_option_safety_issue_suffocation_hazard: bundle.getMessage(
        'issue_type_option_safety_issue_suffocation_hazard',
      ),
      issue_type_option_technical_non_compliance_documentation_missing:
        bundle.getMessage(
          'issue_type_option_technical_non_compliance_documentation_missing',
        ),
      issue_type_option_technical_non_compliance_energy_efficiency_labelling_eel_issue:
        bundle.getMessage(
          'issue_type_option_technical_non_compliance_energy_efficiency_labelling_eel_issue',
        ),
      issue_type_option_technical_non_compliance_labelling: bundle.getMessage(
        'issue_type_option_technical_non_compliance_labelling',
      ),
      issue_type_option_technical_non_compliance_local_language_missing:
        bundle.getMessage(
          'issue_type_option_technical_non_compliance_local_language_missing',
        ),
      issue_type_option_technical_non_compliance_manufacturer_importer_details_missing:
        bundle.getMessage(
          'issue_type_option_technical_non_compliance_manufacturer_importer_details_missing',
        ),
      issue_type_option_technical_non_compliance_marking_instructions:
        bundle.getMessage(
          'issue_type_option_technical_non_compliance_marking_instructions',
        ),
      issue_type_option_technical_non_compliance_other_specify_issue:
        bundle.getMessage(
          'issue_type_option_technical_non_compliance_other_specify_issue',
        ),
      issue_type_option_technical_non_compliance_safety_warning_missing:
        bundle.getMessage(
          'issue_type_option_technical_non_compliance_safety_warning_missing',
        ),
      issue_type_option_unauthorized_prohibited_fraudulent_seller_details:
        bundle.getMessage(
          'issue_type_option_unauthorized_prohibited_fraudulent_seller_details',
        ),
      issue_type_option_unauthorized_prohibited_frequency_level:
        bundle.getMessage(
          'issue_type_option_unauthorized_prohibited_frequency_level',
        ),
      issue_type_option_unauthorized_prohibited_hidden_recording_device:
        bundle.getMessage(
          'issue_type_option_unauthorized_prohibited_hidden_recording_device',
        ),
      issue_type_option_unauthorized_prohibited_ingredient: bundle.getMessage(
        'issue_type_option_unauthorized_prohibited_ingredient',
      ),
      issue_type_option_unauthorized_prohibited_other_specify_issue:
        bundle.getMessage(
          'issue_type_option_unauthorized_prohibited_other_specify_issue',
        ),
      issue_type_option_unauthorized_prohibited_plugs_not_permitted_in_the_country:
        bundle.getMessage(
          'issue_type_option_unauthorized_prohibited_plugs_not_permitted_in_the_country',
        ),
      issue_type_option_unauthorized_prohibited_product_offered_to_non_authorized_recipient_group:
        bundle.getMessage(
          'issue_type_option_unauthorized_prohibited_product_offered_to_non_authorized_recipient_group',
        ),
      issue_type_option_unauthorized_prohibited_registration_licence_missing:
        bundle.getMessage(
          'issue_type_option_unauthorized_prohibited_registration_licence_missing',
        ),
      issue_type_option_unauthorized_prohibited_type_approval_missing:
        bundle.getMessage(
          'issue_type_option_unauthorized_prohibited_type_approval_missing',
        ),
      issue_type_option_unauthorized_prohibited_unauthorized_seller:
        bundle.getMessage(
          'issue_type_option_unauthorized_prohibited_unauthorized_seller',
        ),
      specify_issue_type_field_name: bundle.getMessage(
        'specify_issue_type_field_name',
      ),
      relevant_regulation_field_name: bundle.getMessage(
        'relevant_regulation_field_name',
      ),
      impacted_marketplaces_field_name: bundle.getMessage(
        'impacted_marketplaces_field_name',
      ),
      impacted_marketplaces_option_germany: bundle.getMessage(
        'impacted_marketplaces_option_germany',
      ),
      impacted_marketplaces_option_united_kingdom: bundle.getMessage(
        'impacted_marketplaces_option_united_kingdom',
      ),
      impacted_marketplaces_option_europe_all_countries: bundle.getMessage(
        'impacted_marketplaces_option_europe_all_countries',
      ),
      explanation_field_name: bundle.getMessage('explanation_field_name'),
      affected_products_asins_weburls_field_name: bundle.getMessage(
        'affected_products_asins_weburls_field_name',
      ),
      further_product_information_field_name: bundle.getMessage(
        'further_product_information_field_name',
      ),
      information_request_section_heading: bundle.getMessage(
        'information_request_section_heading',
      ),
      product_information_field_name_information_request: bundle.getMessage(
        'product_information_field_name_information_request',
      ),
      need_by_date_field_name_information_request: bundle.getMessage(
        'need_by_date_field_name_information_request',
      ),
      information_request_checkbox_product_takedown_date_required:
        bundle.getMessage(
          'information_request_checkbox_product_takedown_date_required',
        ),
      information_request_checkbox_number_of_product_sales_required:
        bundle.getMessage(
          'information_request_checkbox_number_of_product_sales_required',
        ),
      information_request_checkbox_details_of_third_party_sellers_required:
        bundle.getMessage(
          'information_request_checkbox_details_of_third_party_sellers_required',
        ),
      information_request_checkbox_information_on_product_stocks_required:
        bundle.getMessage(
          'information_request_checkbox_information_on_product_stocks_required',
        ),
      information_request_checkbox_copy_of_customer_messaging_required:
        bundle.getMessage(
          'information_request_checkbox_copy_of_customer_messaging_required',
        ),
      information_request_checkbox_others_please_specify: bundle.getMessage(
        'information_request_checkbox_others_please_specify',
      ),
      please_specify_details_field_name: bundle.getMessage(
        'please_specify_details_field_name',
      ),
      recall_from_end_user_section_heading: bundle.getMessage(
        'recall_from_end_user_section_heading',
      ),
      meeting_request_section_heading: bundle.getMessage(
        'meeting_request_section_heading',
      ),
      need_by_date_field_name_meeting_request: bundle.getMessage(
        'need_by_date_field_name_meeting_request',
      ),
      describe_key_discussion_points_for_meeting_field_name: bundle.getMessage(
        'describe_key_discussion_points_for_meeting_field_name',
      ),
      other_request_section_heading: bundle.getMessage(
        'other_request_section_heading',
      ),
      need_by_date_field_name_other_request: bundle.getMessage(
        'need_by_date_field_name_other_request',
      ),
      describe_your_request_in_detail_field_name: bundle.getMessage(
        'describe_your_request_in_detail_field_name',
      ),
    });

    return placeHolderReplacer.replace(parseJson(JSON.stringify(schema)));
  }

  private getPlaceholderReplacedCreateCaseStaticSchema(
    schema: CaseCreationStaticSchema,
    bundle: any,
  ): CaseCreationStaticSchema {
    const placeHolderReplacer = new JsonPlaceholderReplacer();

    placeHolderReplacer.addVariableMap({
      referral_information_section_header: bundle.getMessage(
        'referral_information_section_header',
      ),
      full_name_field_name: bundle.getMessage('full_name_field_name'),
      email_field_name: bundle.getMessage('email_field_name'),
      agency_field_name: bundle.getMessage('agency_field_name'),
      country_field_name: bundle.getMessage('country_field_name'),
      role_field_name: bundle.getMessage('role_field_name'),
    });

    return placeHolderReplacer.replace(
      parseJson(JSON.stringify(schema)),
    ) as CaseCreationStaticSchema;
  }
}
